import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';

import { isEmpty, get, forIn } from 'lodash';
import useAPI from 'hooks/useApi';
import AppContext from 'context/Context';
import waterIcon from 'assets/img/icons/water.svg';
import locationIcon from 'assets/img/icons/land.svg';
import soilIcon from 'assets/img/icons/soil_param.svg';
import Enviroment from 'assets/img/icons/Enviroment.svg';
import { getEnvironmentologyMonitoring } from 'api/endpoints/EnvironmentalServices';
import { getHydrologicalMonitoring } from 'api/endpoints/HydrologicalServices';
import {
  getMonitoringLocations,
  getOverallMonitoring,
  getTerrestrialMonitoring
} from 'api/endpoints/TerrestrialServices';
import { getSoilMonitoring } from 'api/endpoints/PedologicalServices';
import { useOutletContext, useParams } from 'react-router-dom';
import { getProjectDetails } from 'api/endpoints/ManagementServices';

import { jwtDecode } from 'jwt-decode';
import MonitoringStats from 'components/dashboards/monitoring/MonitoringStats';
import MonitoringStatsSkeleton from 'components/dashboards/monitoring/skeleton/MonitoringStatsSkeleton';
import ErrorNotFound from 'components/errors/ErrorNotFound';
import StatisticsCard from 'components/dashboards/saas/stats-cards/StatisticsCard';
import WidgetSectionTitle from 'widgets/WidgetSectionTitle';
import StatiticsCardsSkeleton from 'components/dashboards/monitoring/skeleton/StatiticsCardsSekeleton';
import CustomSelect from 'components/common/CustomSelect';
import ObservationRenderer from 'components/dashboards/monitoring/ObservationRender';

const Monitoring = () => {
  const { setConfig } = useContext(AppContext);
  const {
    config: { isDark }
  } = useContext(AppContext);

  const tenant = `0f8e8a4d58f94bf38f56cea9b1fa35fb`;

  const { request_id } = useParams();
  //   const { setApisLoading } = useOutletContext();

  const cluster = '438b5b9c0dd647c09411c313c213dfed';
  const [showErrorPage, setshowErrorPage] = useState(false);

  const [locationData, setLocationData] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const getMonitoringLocationAPI = useAPI(getMonitoringLocations);

  const getProjectDetailsAPI = useAPI(getProjectDetails);
  const getEnvironmentologyMonitoringAPI = useAPI(
    getEnvironmentologyMonitoring
  );
  const getHydrologicalMonitoringAPI = useAPI(getHydrologicalMonitoring);
  const getOverAllMonitoringAPI = useAPI(getOverallMonitoring);
  const getTerrestrialMonitoringAPI = useAPI(getTerrestrialMonitoring);
  const getSoilMonitoringAPI = useAPI(getSoilMonitoring);

  const [locationLoading, setLocationLoading] = useState(false);

  const [ProjectDetails, setProjectDetails] = useState({});
  const [coordinates, setCoordinates] = useState([]);
  const [soilMonitoringData, setSoilMonitoringData] = useState([]);
  const [overAllMonitoringData, setOverAllMonitoringData] = useState({});
  const [environmentologyMonitoringData, setEnvironmentologyMonitoringData] =
    useState({});
  const [hydrologicalMonitoringData, setHydrologicalMonitoringData] = useState(
    {}
  );
  const [terrestrialMonitoringData, setTerrestrialMonitoringData] = useState(
    {}
  );

  const performComparisonLocationAPICall = async () => {
    setLocationLoading(true);
    await getMonitoringLocationAPI.request(tenant);
    setLocationLoading(false);
  };

  useEffect(() => {
    performComparisonLocationAPICall();
  }, []);

  useEffect(() => {
    if (!isEmpty(getMonitoringLocationAPI.data)) {
      const responseData = get(getMonitoringLocationAPI.data, 'results', {});
      const filterOptions = [];
      forIn(responseData, (value, key) => {
        filterOptions.push({ value: key, label: value });
      });
      if (!isEmpty(filterOptions)) {
        setLocationData(filterOptions);
        setSelectedLocation(filterOptions[0]);
      }
    }
  }, [getMonitoringLocationAPI.data]);

  const apiHooks = useMemo(
    () => [
      getOverAllMonitoringAPI,
      getTerrestrialMonitoringAPI,
      getHydrologicalMonitoringAPI,
      getEnvironmentologyMonitoringAPI,
      getSoilMonitoringAPI
    ],
    [
      getOverAllMonitoringAPI,
      getTerrestrialMonitoringAPI,
      getHydrologicalMonitoringAPI,
      getEnvironmentologyMonitoringAPI,
      getSoilMonitoringAPI
    ]
  );

  const performSequentialAPICalls = useCallback(
    async currentCluster => {
      for (const apiHook of apiHooks) {
        await apiHook.request(currentCluster);
      }
    },
    [apiHooks]
  );

  const resetApiStates = () => {
    for (let i = 0; i < apiHooks.length; i++) {
      apiHooks[i].setLoading(true);
    }
  };

  useEffect(() => {
    setshowErrorPage(false);

    if (selectedLocation) {
      resetApiStates(); // Reset loading and data for all APIs
      performSequentialAPICalls(selectedLocation.value);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (
      !isEmpty(getOverAllMonitoringAPI.error) ||
      !isEmpty(getTerrestrialMonitoringAPI.error) ||
      !isEmpty(getHydrologicalMonitoringAPI.error) ||
      !isEmpty(getEnvironmentologyMonitoringAPI.error) ||
      !isEmpty(getProjectDetailsAPI.error) ||
      !isEmpty(getSoilMonitoringAPI.error)
    ) {
      setshowErrorPage(true);
    }
  }, [
    getOverAllMonitoringAPI.error,
    getTerrestrialMonitoringAPI.error,
    getHydrologicalMonitoringAPI.error,
    getEnvironmentologyMonitoringAPI.error,
    getProjectDetailsAPI.error,
    getSoilMonitoringAPI.error
  ]);

  const fetchProjectDetails = useCallback(() => {
    if (selectedLocation && tenant) {
      getProjectDetailsAPI.request({
        tenant,
        request_id: selectedLocation.value
      });
    }
  }, [getProjectDetailsAPI, selectedLocation, tenant]);

  useEffect(() => {
    if (selectedLocation && tenant) {
      fetchProjectDetails();
    }
  }, [tenant, selectedLocation]);

  useEffect(() => {
    const data = get(getProjectDetailsAPI.data, 'results', {});
    setProjectDetails(data);
  }, [getProjectDetailsAPI.data]);

  useEffect(() => {
    if (ProjectDetails && ProjectDetails.coordinates) {
      const isMultiPolygon =
        ProjectDetails.coordinates.includes('MULTIPOLYGON');

      if (isMultiPolygon) {
        const coordinatesRegex = /\(\((.*?)\)\)/g;
        const matches = ProjectDetails.coordinates.match(coordinatesRegex);

        if (matches) {
          const formattedCoordinates = matches.map(match => {
            const coordinatesArray = match.match(/-?\d+\.\d+/g).map(Number);

            const formatted = [];
            for (let i = 0; i < coordinatesArray.length; i += 2) {
              formatted.push([coordinatesArray[i + 1], coordinatesArray[i]]);
            }
            return formatted;
          });
          setCoordinates(formattedCoordinates);
        }
      } else {
        const coordinatesArray = ProjectDetails?.coordinates
          .match(/-?\d+\.\d+/g)
          .map(Number);
        const formattedCoordinates = [];
        for (let i = 0; i < coordinatesArray.length; i += 2) {
          formattedCoordinates.push([
            coordinatesArray[i + 1],
            coordinatesArray[i]
          ]);
        }
        setCoordinates(formattedCoordinates);
      }
    }
  }, [ProjectDetails]);

  useEffect(() => {
    if (!isEmpty(getOverAllMonitoringAPI.data)) {
      setOverAllMonitoringData(
        get(getOverAllMonitoringAPI.data, 'results', {})
      );
    }
    const reportUrl = get(
      getOverAllMonitoringAPI.data,
      'results.assessment_report',
      ''
    );
    setConfig('pdfReportUrl', reportUrl);
  }, [getOverAllMonitoringAPI.data]);

  useEffect(() => {
    if (!isEmpty(getSoilMonitoringAPI.data)) {
      setSoilMonitoringData(get(getSoilMonitoringAPI.data, 'results', {}));
    }
  }, [getSoilMonitoringAPI.data]);
  useEffect(() => {
    if (!isEmpty(getTerrestrialMonitoringAPI.data)) {
      setTerrestrialMonitoringData(
        get(getTerrestrialMonitoringAPI.data, 'results', {})
      );
    }
  }, [getTerrestrialMonitoringAPI.data]);

  useEffect(() => {
    if (!isEmpty(getEnvironmentologyMonitoringAPI.data)) {
      setEnvironmentologyMonitoringData(
        get(getEnvironmentologyMonitoringAPI.data, 'results', {})
      );
    }
  }, [getEnvironmentologyMonitoringAPI.data]);

  useEffect(() => {
    if (!isEmpty(getHydrologicalMonitoringAPI.data)) {
      setHydrologicalMonitoringData(
        get(getHydrologicalMonitoringAPI.data, 'results', {})
      );
    }
  }, [getHydrologicalMonitoringAPI.data]);

  // Assuming your array is called features

  const environmentologyObservation = get(
    environmentologyMonitoringData,
    'environmentology_observation'
  );

  const terrestrialObservation = get(
    terrestrialMonitoringData,
    'terrestrial_observation',
    {}
  );
  const hydrologicalObservation = get(
    hydrologicalMonitoringData,
    'hydrological_observation',
    {}
  );
  const Pedological_observation = get(
    soilMonitoringData,
    'pedological_observation'
  );

  let waterStressMapData = {};

  forIn(hydrologicalObservation, (value, key) => {
    if (key === 'water_stress') {
      waterStressMapData = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key
      };
    }
  });

  const overallScore = get(overAllMonitoringData, 'overall_score', {});

  const overallData = [];
  forIn(overallScore, (value, key) => {
    overallData.push({
      title: key === 'green_cover' ? 'Green Cover' : key,
      suffix: '%',
      ...overallScore[key]
    });
  });

  return (
    <>
      {showErrorPage ? (
        <ErrorNotFound />
      ) : (
        <>
          <Row className="mb-3 mt-3">
            <Col xs={12}>
              <Row className="g-3 g-lg-4">
                <Col lg={3}>
                  <CustomSelect
                    isClearable={false}
                    placeholder="Select BaseLine"
                    classNamePrefix="react-select"
                    // isDisabled={loading}
                    value={selectedLocation}
                    options={locationData}
                    onChange={value => {
                      setSelectedLocation(value);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="g-3 mb-3 mt-1">
            <Col md="12" className="mt-0">
              {isEmpty(overAllMonitoringData?.asessment_details) ||
              getOverAllMonitoringAPI.loading ? (
                <MonitoringStatsSkeleton />
              ) : (
                <MonitoringStats
                  data={overAllMonitoringData?.asessment_details}
                />
              )}
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            {getOverAllMonitoringAPI.loading ? (
              <StatiticsCardsSkeleton />
            ) : !isEmpty(overallData) ? (
              overallData?.map((stat, index) => (
                <Col md={4} key={index}>
                  <StatisticsCard
                    stat={stat}
                    style={{ minWidth: '12rem', height: '100%' }}
                    index={index}
                  />
                </Col>
              ))
            ) : (
              <></>
            )}
          </Row>
          {!isEmpty(terrestrialObservation) && (
            <WidgetSectionTitle
              // icon={locationIcon}
              title="Vegetation Parameters"
              transform="shrink-4"
              className="mb-4 mt-4 align-items-center"
            />
          )}
          <Row className="g-3 mb-3">
            <ObservationRenderer
              key={'terrestrialObservation'}
              observation={terrestrialObservation}
              loading={getTerrestrialMonitoringAPI.loading}
              coordinates={coordinates}
            />
          </Row>
          {!isEmpty(hydrologicalObservation) && (
            <WidgetSectionTitle
              // icon={locationIcon}
              title="Water Availability"
              transform="shrink-4"
              className="mb-4 mt-4 align-items-center"
              light={true}
            />
          )}

          <Row className="g-3 mb-3">
            <ObservationRenderer
              key={'hydrologicalObservation'}
              observation={hydrologicalObservation}
              loading={getHydrologicalMonitoringAPI.loading}
              coordinates={coordinates}
            />
          </Row>
          {!isEmpty(environmentologyObservation) && (
            <WidgetSectionTitle
              title="Air Quality"
              transform="shrink-4"
              className="mb-4 mt-4 align-items-center"
            />
          )}
          <Row className="g-3 mb-3">
            <ObservationRenderer
              key={'environmentologyObservation'}
              observation={environmentologyObservation}
              loading={getEnvironmentologyMonitoringAPI.loading}
              coordinates={coordinates}
            />
          </Row>

          {!isEmpty(Pedological_observation) && (
            <WidgetSectionTitle
              title="Soil Parameters"
              transform="shrink-4"
              className="mb-4 mt-4 align-items-center"
            />
          )}
          <Row className="g-3 mb-3">
            <ObservationRenderer
              key={'Pedological_observation'}
              observation={Pedological_observation}
              loading={getSoilMonitoringAPI.loading}
              coordinates={coordinates}
            />
          </Row>
        </>
      )}
    </>
  );
};

export default Monitoring;
